/**
 * Wix legacy URL migration.
 *
 * Wix service pages are all served under the /service-page/<slug> pattern.
 * When the site was on Wix, hundreds of external inbound links + Google
 * indexed URLs pointed to these old paths. Losing them causes 404s + SEO
 * link-equity loss.
 *
 * This module contains:
 *   1. A slug → new-path map covering every known Wix /service-page/ URL.
 *      Any unmatched slug falls back to /services (better than a 404).
 *   2. A helper the App-level router uses to resolve a live-URL at runtime.
 *
 * Add new mappings here as they are discovered — no other file changes.
 *
 * Sources feeding this map:
 *   - the client's own Wix sitemap dump
 *   - Ubersuggest / Google Search Console "old URLs" report
 *   - inbound-link tools (Ahrefs, Moz) for referring URLs that 404
 *   - anecdotal user reports (e.g. /service-page/free-consultation)
 */

// Slug (lowercased, trailing-slash stripped) → destination path on the new site.
const SERVICE_PAGE_MAP = {
  // Booking / consultation flows
  "free-consultation":                                "/contact",
  "book-a-free-consultation":                         "/contact",
  "book-consultation":                                "/contact",
  "consultation":                                     "/contact",
  "book-a-call":                                      "/contact",
  "video-production":                                 "/services/video-production-company-cape-town",
  "video-production-services":                        "/services/video-production-company-cape-town",
  "video-production-company":                         "/services/video-production-company-cape-town",
  "corporate-video":                                  "/services/corporate-video",
  "corporate-video-production":                       "/services/corporate-video",
  "brand-films":                                      "/services/brand-films",
  "brand-film":                                       "/services/brand-films",
  "commercials":                                      "/services/commercials",
  "commercial":                                       "/services/commercials",
  "commercial-production":                            "/services/commercials",
  "tv-commercials":                                   "/services/commercials",
  "pre-production":                                   "/services",
  "post-production":                                  "/services/post-production",
  "editing":                                          "/services/post-production",
  "video-editing":                                    "/services/post-production",
  "documentary":                                      "/services/documentary-filmmaking",
  "documentary-filmmaking":                           "/services/documentary-filmmaking",
  "documentary-production":                           "/services/documentary-filmmaking",
  "photography":                                      "/photography",
  "photographer":                                     "/photography",
  "commercial-photography":                           "/photography",
  "product-photography":                              "/photography",
  "food-photography":                                 "/photography",
  "hair-makeup":                                      "/services/production-crew-support",
  "hair-and-makeup":                                  "/services/production-crew-support",
  "makeup-artist":                                    "/services/production-crew-support",
  "art-direction":                                    "/services/production-crew-support",
  "vehicle-rental":                                   "/services/production-crew-support",
  "vehicle-rental-1":                                 "/services/production-crew-support",
  "production-vehicle":                               "/services/production-crew-support",
  "crew":                                             "/services/production-crew-support",
  "production-crew":                                  "/services/production-crew-support",
  "talent":                                           "/services/models-talent",
  "models":                                           "/services/models-talent",
  "casting":                                          "/services/models-talent",
  "ai-video-editing":                                 "/services/ai-video-editing",
  "ai-video":                                         "/services/ai-video-editing",
  "videographer":                                     "/services/videographer-cape-town",
  "videographer-cape-town":                           "/services/videographer-cape-town",
  "video-production-agency":                          "/services/video-production-agency",
  // Niche vertical landing pages
  "hospitality":                                      "/for/hotels-and-lodges",
  "hotels":                                           "/for/hotels-and-lodges",
  "lodges":                                           "/for/hotels-and-lodges",
  "luxury-hospitality-visibility-engine":             "/for/hotels-and-lodges",
  "medical":                                          "/for/medical-dental-pharma",
  "dental":                                           "/for/medical-dental-pharma",
  "pharma":                                           "/for/medical-dental-pharma",
  "healthcare":                                       "/for/medical-dental-pharma",
  "medical-video":                                    "/for/medical-dental-pharma",
  "construction":                                     "/for/construction-services",
  "construction-video":                               "/for/construction-services",
  "trades":                                           "/for/construction-services",
  "builders":                                         "/for/construction-services",
  "developers":                                       "/for/construction-services",
  "marketing":                                        "/for/marketing-agencies",
  "marketing-agencies":                               "/for/marketing-agencies",
  "agencies":                                         "/for/marketing-agencies",
  "agency":                                           "/for/marketing-agencies",
};

// Legacy bare-slug paths from the old Wix site (already partly covered in
// SLUG_REDIRECTS + App.js Routes — this list is the exhaustive belt-and-braces
// version that RedirectFromOldSlugs will hit BEFORE React Router tries to
// match a Route.)
export const WIX_LEGACY_BARE_PATHS = {
  "/videographer-services-cape-town":                 "/services",
  "/videographer-cape-town-portfolio":                "/portfolio",
  "/videographer-cape-town-prices":                   "/pricing",
  "/videographer-cape-town":                          "/services/videographer-cape-town",
  "/cape-town-video-production-company-portfolio":    "/portfolio",
  "/photographer-cape-town":                          "/photography",
  "/photographer-cape-town-portfolio":                "/photography",
  "/plans-and-pricing":                               "/pricing",
  "/pricing-plans":                                   "/pricing",
  "/contact-us-pie-in-the-sky-productions":           "/contact",
  "/contact-us":                                      "/contact",
  "/free-consultation":                               "/contact",
  "/tools-tips":                                      "/blog",
  "/privacy-policy":                                  "/privacy",
  "/terms":                                           "/working-agreement",
  "/terms-of-service":                                "/working-agreement",
  "/testimonials":                                    "/references",
  "/hospitality":                                     "/for/hotels-and-lodges",
  "/luxury-hospitality-visibility-engine":            "/for/hotels-and-lodges",
  "/photos":                                          "/photography",
  "/photo-portfolio":                                 "/photography",
};

/**
 * Resolve a Wix legacy URL to a modern destination.
 * Returns null if the URL is NOT a Wix legacy pattern (so the caller can
 * fall through to normal React Router matching / 404).
 */
export const resolveWixRedirect = (pathname) => {
  if (!pathname) return null;
  const p = pathname.replace(/\/+$/, "").toLowerCase() || "/";

  // Bare legacy paths
  if (WIX_LEGACY_BARE_PATHS[p]) return WIX_LEGACY_BARE_PATHS[p];

  // /service-page/<slug>  (Wix's default template for every service page)
  const m = p.match(/^\/service-page\/([^/?#]+)$/);
  if (m) {
    const slug = m[1];
    return SERVICE_PAGE_MAP[slug] || "/services";
  }

  // /post/<slug> already handled by our own Route
  // /blog/<slug>  → /blog (App.js Route)
  return null;
};
